import { useEffect, useRef, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { CARD, CARD_CLASS, TONE, type NoticeTone } from '@shared/ui/kit';

// Phase 36 §22 — the chrome the four settings sub-pages (22a–22d) share.
//
// Thin wrappers over `@shared/ui/kit`, not a second dialect. The card surface,
// the mono field label and the section head are the three shapes that repeat on
// every one of the artboards, and stating them once is what stops the sections
// drifting apart the way the ten of them had before.
//
// Nothing here reads or writes data, and nothing here is a field. Every screen
// keeps exactly the values and handlers it already had; this file only changes
// what they look like.

/** The one surface. Every panel on a settings page sits on this and nothing else. */
export function SettingsCard({
  children,
  className = '',
  padded = true,
  style,
}: {
  children: ReactNode;
  className?: string;
  /** Off for cards whose own header/body/foot bands supply the padding. */
  padded?: boolean;
  style?: CSSProperties;
}) {
  return (
    <div
      className={`${CARD_CLASS} ${padded ? 'px-[22px] py-5' : ''} ${className}`}
      style={{ ...CARD, ...style }}
    >
      {children}
    </div>
  );
}

/**
 * A section's title, what it is for, and its actions.
 *
 * The description is not optional decoration: a settings section that does not
 * say what it changes downstream is how a compliance figure moves overnight
 * with nobody able to say which switch did it.
 */
export function SectionHead({
  title,
  description,
  actions,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    // Wraps: when the actions would squeeze the text under ~320px (Account at
    // 1024, where the description ran to four lines) they drop to a row of
    // their own, still right-aligned, and the text takes the full width.
    <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
      <div className="min-w-[min(100%,320px)] flex-1">
        <h2
          className="text-[18px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)]"
          style={{ fontFamily: 'var(--co-font-display)' }}
        >
          {title}
        </h2>
        {description && (
          <p
            className="mt-[5px] max-w-[84ch] text-[12.5px] leading-[1.55] text-[var(--co-ink-3)]"
            style={{ textWrap: 'pretty' } as CSSProperties}
          >
            {description}
          </p>
        )}
      </div>
      {actions && <div className="ml-auto flex flex-none items-center gap-2">{actions}</div>}
    </div>
  );
}

/** The field label. Mono, so a label can never be mistaken for a value. */
export function FieldLabel({
  children,
  htmlFor,
}: {
  children: ReactNode;
  htmlFor?: string;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className="co-mono block text-[9px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]"
    >
      {children}
    </label>
  );
}

/**
 * A read-only value shaped like the field it would be if it were editable.
 *
 * Used where a value is displayed but not changed here (organisation name and
 * slug, for example, which are owned by the platform). Shaping it like a
 * disabled input rather than bare text says *why* it cannot be typed into.
 */
export function ReadOnlyValue({
  children,
  mono = false,
}: {
  children: ReactNode;
  mono?: boolean;
}) {
  return (
    <div
      className={`flex h-[34px] items-center rounded-lg border border-[var(--co-line-subtle)] bg-[var(--co-sunken)] px-3 text-[13px] text-[var(--co-ink-2)] ${
        mono ? 'co-mono text-[12px]' : ''
      }`}
    >
      <span className="truncate">{children}</span>
    </div>
  );
}

/**
 * A switch row: what it does, what turns it, and what it costs.
 *
 * The consequence line sits under the label rather than in a tooltip because
 * the reader decides here — a toggle whose effect is only discoverable on hover
 * is a toggle that gets flipped without the effect being read.
 */
export function ToggleRow({
  label,
  description,
  control,
  last = false,
}: {
  label: ReactNode;
  description?: ReactNode;
  control: ReactNode;
  last?: boolean;
}) {
  return (
    <div
      className={`flex items-start gap-5 py-[13px] ${
        last ? '' : 'border-b border-[var(--co-line-subtle)]'
      }`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold text-[var(--co-ink)]">{label}</p>
        {description && (
          <p
            className="mt-[4px] max-w-[92ch] text-[12px] leading-[1.55] text-[var(--co-ink-3)]"
            style={{ textWrap: 'pretty' } as CSSProperties}
          >
            {description}
          </p>
        )}
      </div>
      <div className="flex-none pt-[2px]">{control}</div>
    </div>
  );
}

/** The dimmed stand-in for a panel whose values have not arrived yet. */
export function LoadingBlock({ height = 40 }: { height?: number }) {
  return (
    <div
      aria-busy="true"
      className="rounded-lg bg-[var(--co-line-subtle)]"
      style={{ height, opacity: 0.45 }}
    />
  );
}

// ------------------------------------------------------------------ dialogs
//
// The confirm/compose popups these screens already had, in the kit's surface.
// They are here rather than in `@shared/ui/kit` because they carry no new
// behaviour: every one of them is still mounted with `createPortal` by the
// screen that owns it, so the fixed backdrop covers the viewport even when an
// ancestor has a transform (which would otherwise make `fixed` resolve against
// that ancestor rather than the window).

export function Backdrop({ children }: { children: ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(20,18,14,.55)] p-4">
      {children}
    </div>
  );
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Dialog({
  labelledBy,
  width = 460,
  children,
  /**
   * Bug 8796 (a11y) — called on Escape. Optional so a Dialog with nothing
   * meaningful to close (none exist today, but the type stays honest) simply
   * doesn't get the keydown wired.
   */
  onClose,
}: {
  labelledBy: string;
  width?: number;
  children: ReactNode;
  onClose?: () => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);

  // Bug 8796 (a11y): move focus INTO the dialog on open. Every Dialog is
  // portalled to document.body, so without this the browser leaves focus on
  // whatever page button opened it — a screen reader user gets no signal
  // that a new surface appeared, and a keyboard user's next Tab still starts
  // from the page behind the modal.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const first = root.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    // Fall back to the dialog surface itself (via tabIndex=-1 below) so
    // SOMETHING inside the modal always has focus, even a content-only
    // dialog with no interactive element yet (e.g. a "Checking..." loading
    // state).
    (first ?? root).focus();
  }, []);

  // Bug 8796 (a11y): trap Tab/Shift+Tab within the dialog, and close on
  // Escape when the caller gave us a way to. Without a trap, Tab walks out
  // into the page behind an open modal — the July QA addendum's exact
  // finding ("Tab 8x moved focus OUT of the modal every time").
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      if (onClose) {
        e.stopPropagation();
        onClose();
      }
      return;
    }
    if (e.key !== 'Tab') return;
    const root = rootRef.current;
    if (!root) return;
    const focusable = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    // document.activeElement, not e.target: a synthetic Tab keydown's target
    // is the element about to LOSE focus, which is what we need to compare.
    if (e.shiftKey) {
      if (document.activeElement === first || !root.contains(document.activeElement)) {
        e.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      className={`max-h-full w-full overflow-y-auto ${CARD_CLASS}`}
      style={{ ...CARD, maxWidth: width, boxShadow: 'var(--co-shadow-pop)' }}
    >
      {children}
    </div>
  );
}

export function DialogHead({
  id,
  title,
  tone,
  onClose,
}: {
  id: string;
  title: string;
  /** Marks a consequence — omit for a plain compose dialog. */
  tone?: NoticeTone;
  onClose?: () => void;
}) {
  const t = tone ? TONE[tone] : null;
  return (
    <div className="flex items-center gap-3 border-b border-[var(--co-line-subtle)] px-6 py-4">
      {t && (
        <div
          className="flex h-[26px] w-[26px] flex-none items-center justify-center rounded-md text-[14px] font-bold text-[var(--co-ink-invert)]"
          style={{ background: t.ink }}
          aria-hidden="true"
        >
          !
        </div>
      )}
      <h3
        id={id}
        className="min-w-0 flex-1 truncate text-[16px] font-bold tracking-[-.01em] text-[var(--co-ink)]"
        style={{ fontFamily: 'var(--co-font-display)' }}
      >
        {title}
      </h3>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close dialog"
          className="flex-none rounded-md p-[6px] text-[var(--co-ink-3)] transition-colors hover:bg-[var(--co-selected)] hover:text-[var(--co-ink)]"
        >
          <span aria-hidden="true">✕</span>
        </button>
      )}
    </div>
  );
}

export function DialogFoot({ children }: { children: ReactNode }) {
  return (
    <div className="flex justify-end gap-3 border-t border-[var(--co-line-subtle)] bg-[var(--co-selected)] px-6 py-4">
      {children}
    </div>
  );
}

/**
 * A native `<select>`, restyled to the kit's field.
 *
 * The kit's `SelectButton` is a button plus a popup; swapping to it here would
 * replace working native keyboard and mobile behaviour with a reimplementation,
 * so these screens keep the real element and only change how it looks.
 */
export const FILTER_SELECT =
  'h-[34px] flex-none rounded-lg border border-[var(--co-line-strong)] bg-[var(--co-raised)] ' +
  'px-[10px] text-[12.5px] text-[var(--co-ink)] outline-none transition-colors ' +
  'focus:border-[var(--co-brass)]';

const MENU_ITEM_BASE =
  'flex w-full items-center gap-2 px-4 py-[9px] text-left text-[12.5px] transition-colors ' +
  'hover:bg-[var(--co-selected)]';

export const MENU_ITEM = `${MENU_ITEM_BASE} text-[var(--co-ink-2)]`;
export const MENU_ITEM_OK = `${MENU_ITEM_BASE} text-[var(--co-green-ink)]`;
export const MENU_ITEM_DANGER = `${MENU_ITEM_BASE} text-[var(--co-burgundy)]`;
